import React, { FC, useEffect, useState } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import {
  GetPrsesToPayQueryResult,
  PaymentDescription,
  PayMethodType,
  PrsPaymentSettings,
  usePayMutation,
} from 'store/post-results-services/postResultsServicesApi';
import { formatUTCDate, ROUTES } from 'core';
import DOMPurify from 'dompurify';

import { IcoLock, Icon } from '@britishcouncil/react-solas';

import * as S from './SelectPaymentMainSection.styles';
import { PayMethod } from './PayMethod';

export const SelectPaymentMainSection = ({
  prses,
  prsPaymentSettings,
  showBackButton,
}: {
  prses: GetPrsesToPayQueryResult;
  prsPaymentSettings: PrsPaymentSettings;
  showBackButton: boolean;
}) => {
  const [payMethod, setPayMethod] = useState<PayMethodType | undefined>(undefined);
  const navigate = useNavigate();

  const [pay, { isSuccess, isLoading: submitting, data }] = usePayMutation();

  const payOnline = () => {
    pay({
      prsIds: prses.prss.map((x) => x.id),
      currencyCode: prses.currency,
      fee: parseFloat(prses.total),
    });
  };

  useEffect(() => {
    if (isSuccess && data) {
      window.location.replace(data.paymentUrl);
    }
  }, [data, isSuccess]);

  return (
    <S.Root>
       {parseFloat(prses.total) === 0  &&  (
        <>
          <div className="row">
            <h4> No payment is required</h4>
          </div>
          <hr />
          <S.ReturnButton
            variant="secondary"
            iconLeft={<FaChevronLeft />}
            borderRadius={'rounded'}
            onClick={() => navigate(generatePath(ROUTES.POST_RESULTS_SERVICES))}
          >
            Back
          </S.ReturnButton>
        </>
      )}

      {parseFloat(prses.total) !== 0  && (
        <>
      <div className="row">
        <h4>Please select preferred payment method to complete payment</h4>
        <h5>
          Payment deadline: <S.Deadline>{formatUTCDate(prses.deadline)}</S.Deadline>
        </h5>
      </div>
      {prsPaymentSettings.allowOnlinePayment && (
        <PayMethod
          title="Online payment"
          subtitle={
            payMethod === 'Online'
              ? 'You selected an online payment method'
              : 'Select this checkbox if you want to pay online'
          }
          selected={payMethod === 'Online'}
          payMethod="Online"
          onSelected={setPayMethod}
          buttonText="Pay now"
          blockAction={submitting || !!data?.paymentUrl}
          onAction={payOnline}
          description={<OnlinePaymentDescription descriptions={prsPaymentSettings.onlinePaymentsDescriptions} />}
        />
      )}
      {prsPaymentSettings.allowOfflinePayment && (
        <PayMethod
          title="Offline payment"
          subtitle={
            payMethod === 'Offline'
              ? 'You selected an offline payment method'
              : 'Select this checkbox if you want to pay offline'
          }
          selected={payMethod === 'Offline'}
          payMethod="Offline"
          onSelected={setPayMethod}
          buttonText="Pay later & go to summary"
          blockAction={submitting}
          onAction={() => navigate(ROUTES.POST_RESULTS_SERVICES)}
          description={<OfflinePaymentDescription paymentMethods={prsPaymentSettings.offlinePaymentsDescriptions} />}
        />
      )}
      </>
      )}
      {showBackButton && (
        <>
          <hr />
          <S.ReturnButton
            variant="secondary"
            iconLeft={<FaChevronLeft />}
            borderRadius={'rounded'}
            onClick={() => navigate(generatePath(ROUTES.POST_RESULTS_SERVICES))}
          >
            Back
          </S.ReturnButton>
        </>
      )}
    </S.Root>
  );
};

const OnlinePaymentDescription: FC<{ descriptions: PaymentDescription[] }> = ({ descriptions }) => (
  <>
    <p>We accept Visa and Mastercard credit and debit cards.</p>
    {descriptions?.map((item, i) => (
      <div key={i}>
        <p>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(item.description, {
                ADD_ATTR: ['target'],
              }),
            }}
          />
        </p>
      </div>
    ))}
    <p>
      <Icon size="small" fitted>
        <IcoLock />
      </Icon>{' '}
      You will be directed to a secure payment page.
    </p>
  </>
);

const OfflinePaymentDescription: FC<{
  paymentMethods: { name: string; description: string }[];
}> = ({ paymentMethods }) => (
  <>
    <p>
      <strong>If you choose 'Offline payment', registration is not complete until payment is received.</strong>
    </p>
    <p>Ways to pay:</p>
    {paymentMethods.map((item, i) => (
      <div key={i}>
        <p>
          <strong>{item.name}</strong>
        </p>
        <p>
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(item.description, {
                ADD_ATTR: ['target'],
              }),
            }}
          />
        </p>
      </div>
    ))}
  </>
);
